import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const MAIN_SITE_URL = 'https://kaspreadsheet.com';

const categoryLinks = [
  { key: 'categories_shoes', label: 'Shoes', url: 'https://kaspreadsheet.com/category-911-Shoes.html', icon: 'ri-footprint-line' },
  { key: 'categories_hoodies', label: 'Hoodies/Sweaters', url: 'https://kaspreadsheet.com/category-912-Hoodies%2FSweaters.html', icon: 'ri-shirt-line' },
  { key: 'categories_tshirts', label: 'T-Shirts', url: 'https://kaspreadsheet.com/category-913-T-Shirts.html', icon: 'ri-t-shirt-line' },
  { key: 'categories_jackets', label: 'Jackets', url: 'https://kaspreadsheet.com/category-914-Jackets.html', icon: 'ri-shirt-line' },
  { key: 'categories_pants', label: 'Pants/Shorts', url: 'https://kaspreadsheet.com/category-915-Pants%2FShorts.html', icon: 'ri-shirt-line' },
  { key: 'categories_headwear', label: 'Headwear', url: 'https://kaspreadsheet.com/category-916-Headwear.html', icon: 'ri-glasses-line' },
  { key: 'categories_sets', label: 'Sets', url: 'https://kaspreadsheet.com/category-917-Sets.html', icon: 'ri-stack-line' },
  { key: 'categories_underwear', label: 'Underwear', url: 'https://kaspreadsheet.com/category-918-underwear%2Funderpants.html', icon: 'ri-shirt-line' },
  { key: 'categories_jersey', label: 'Jersey', url: 'https://kaspreadsheet.com/category-919-Jersey.html', icon: 'ri-t-shirt-line' },
  { key: 'categories_accessories', label: 'Accessories', url: 'https://kaspreadsheet.com/category-920-Accessories.html', icon: 'ri-vip-diamond-line' },
  { key: 'categories_bags', label: 'Bags', url: 'https://kaspreadsheet.com/category-921-Bags.html', icon: 'ri-shopping-bag-3-line' },
  { key: 'categories_belts', label: 'Belts', url: 'https://kaspreadsheet.com/category-922-Belts.html', icon: 'ri-links-line' },
  { key: 'categories_perfumes', label: 'Perfumes', url: 'https://kaspreadsheet.com/category-923-Perfumes.html', icon: 'ri-drop-line' },
  { key: 'categories_other', label: 'Other Stuff', url: 'https://kaspreadsheet.com', icon: 'ri-more-line' },
];

const languages = [
  { code: 'pt-br', label: 'nav_portuguese', path: '/' },
  { code: 'en', label: 'nav_english', path: '/en' },
  { code: 'de', label: 'nav_german', path: '/de' },
];

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);
  const [mobileCategoriesOpen, setMobileCategoriesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const currentLang = languages.find(l => l.code === i18n.language) || languages[1];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-shadow duration-300 ${
        scrolled ? 'shadow-md' : 'shadow-sm'
      } bg-white/95 backdrop-blur-md border-b border-background-200`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a href="/" className="flex items-center gap-2 flex-shrink-0">
            <span className="text-lg md:text-xl font-bold text-primary-600 whitespace-nowrap">
              {t('nav_brand')}
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-2">
            {/* Home */}
            <a
              href="/"
              className="px-3 py-2 rounded-md text-sm font-medium text-foreground-700 hover:text-primary-600 hover:bg-primary-50 transition-colors whitespace-nowrap"
            >
              {t('nav_home')}
            </a>

            {/* Categories Dropdown */}
            <div className="relative">
              <button
                onClick={() => {
                  setCategoriesOpen(!categoriesOpen);
                  setLanguageOpen(false);
                }}
                className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-foreground-700 hover:text-primary-600 hover:bg-primary-50 transition-colors whitespace-nowrap"
              >
                {t('nav_categories')}
                <i className={`ri-arrow-down-s-line transition-transform ${categoriesOpen ? 'rotate-180' : ''}`} />
              </button>

              {categoriesOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 max-h-96 overflow-y-auto bg-white rounded-lg shadow-lg border border-background-200 py-2 animate-slide-down">
                  {categoryLinks.map((cat) => (
                    <a
                      key={cat.key}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors"
                      onClick={() => setCategoriesOpen(false)}
                    >
                      <i className={`${cat.icon} text-base text-primary-400 w-5 h-5 flex items-center justify-center`} />
                      {t(cat.key)}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Shopping Guide */}
            <a
              href="/article/cssbuy-buying-guide"
              className="px-3 py-2 rounded-md text-sm font-medium text-foreground-700 hover:text-primary-600 hover:bg-primary-50 transition-colors whitespace-nowrap"
            >
              {t('nav_shopping_guide')}
            </a>

            {/* Main Site Button */}
            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 rounded-md text-sm font-medium bg-primary-500 text-white hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              CSSBuy Spreadsheet
            </a>

            {/* Language Switcher */}
            <div className="relative">
              <button
                onClick={() => {
                  setLanguageOpen(!languageOpen);
                  setCategoriesOpen(false);
                }}
                className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-foreground-700 hover:text-primary-600 hover:bg-primary-50 transition-colors whitespace-nowrap"
              >
                <i className="ri-global-line" />
                {t(currentLang.label)}
                <i className={`ri-arrow-down-s-line transition-transform ${languageOpen ? 'rotate-180' : ''}`} />
              </button>

              {languageOpen && (
                <div className="absolute top-full right-0 mt-2 w-40 bg-white rounded-lg shadow-lg border border-background-200 py-2 animate-slide-down">
                  {languages.map((lang) => (
                    <a
                      key={lang.code}
                      href={lang.path}
                      className={`block px-4 py-2 text-sm transition-colors ${
                        i18n.language === lang.code
                          ? 'text-primary-600 bg-primary-50 font-medium'
                          : 'text-foreground-700 hover:bg-primary-50 hover:text-primary-600'
                      }`}
                      onClick={() => setLanguageOpen(false)}
                    >
                      {t(lang.label)}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 rounded-md text-foreground-700 hover:bg-background-100 transition-colors"
            aria-label="Menu"
          >
            <i className={`ri-${mobileMenuOpen ? 'close' : 'menu'}-line text-2xl w-6 h-6 flex items-center justify-center`} />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-background-200 shadow-lg animate-slide-down">
          <div className="px-4 py-4 space-y-3 max-h-[80vh] overflow-y-auto">
            {/* Quick links */}
            <div className="grid grid-cols-2 gap-2">
              <a
                href="/"
                className="flex items-center gap-2 px-4 py-3 bg-background-50 rounded-lg text-sm font-medium text-foreground-800 hover:bg-primary-50 hover:text-primary-600 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                <i className="ri-home-5-line text-lg w-5 h-5 flex items-center justify-center" />
                {t('nav_home')}
              </a>
              <a
                href="/article/cssbuy-buying-guide"
                className="flex items-center gap-2 px-4 py-3 bg-background-50 rounded-lg text-sm font-medium text-foreground-800 hover:bg-primary-50 hover:text-primary-600 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                <i className="ri-guide-line text-lg w-5 h-5 flex items-center justify-center" />
                {t('nav_shopping_guide')}
              </a>
            </div>

            {/* Categories accordion */}
            <button
              onClick={() => setMobileCategoriesOpen(!mobileCategoriesOpen)}
              className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium text-foreground-800 hover:bg-background-50 transition-colors"
            >
              <span className="flex items-center gap-2">
                <i className="ri-grid-line text-lg w-5 h-5 flex items-center justify-center text-primary-500" />
                {t('nav_categories')}
              </span>
              <i className={`ri-arrow-down-s-line transition-transform ${mobileCategoriesOpen ? 'rotate-180' : ''}`} />
            </button>

            {mobileCategoriesOpen && (
              <div className="grid grid-cols-2 gap-1 pl-2 animate-slide-down">
                {categoryLinks.map((cat) => (
                  <a
                    key={cat.key}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2 px-3 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 rounded-lg transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <i className={`${cat.icon} text-base text-primary-400 w-5 h-5 flex items-center justify-center`} />
                    {t(cat.key)}
                  </a>
                ))}
              </div>
            )}

            {/* Main site CTA */}
            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              <i className="ri-external-link-line w-5 h-5 flex items-center justify-center" />
              CSSBuy Spreadsheet
            </a>

            {/* Language */}
            <div className="border-t border-background-200 pt-3">
              <div className="text-xs font-medium text-foreground-500 uppercase tracking-wider mb-2">
                {t('nav_language')}
              </div>
              <div className="flex gap-2">
                {languages.map((lang) => (
                  <a
                    key={lang.code}
                    href={lang.path}
                    className={`flex-1 text-center px-3 py-2 rounded-lg text-sm transition-colors ${
                      i18n.language === lang.code
                        ? 'bg-primary-50 text-primary-600 font-medium'
                        : 'text-foreground-700 hover:bg-background-100'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t(lang.label)}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}